/**
 * useRenderOptions Hook
 * Fetches dynamic options for render wizard from the database
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase-client';
import type { EnhanceToggle, ModeOption, InteriorStyle } from '../../types/render-options';

// Re-export for consumers that import from this file
export type { EnhanceToggle, ModeOption, InteriorStyle };

interface UseRenderOptionsReturn {
    // Data
    enhanceToggles: EnhanceToggle[];
    stagingPersonas: ModeOption[];
    renovationStyles: ModeOption[];
    interiorStyles: InteriorStyle[];

    // Loading states
    loading: boolean;
    error: string | null;

    // Computed
    getDefaultToggles: () => Record<string, boolean>;
    getPromptFragments: (enabledToggles: Record<string, boolean>) => string;
    getStylePromptValue: (mode: string, key: string) => string;

    // Actions
    refresh: () => Promise<void>;
}

export function useRenderOptions(): UseRenderOptionsReturn {
    const [enhanceToggles, setEnhanceToggles] = useState<EnhanceToggle[]>([]);
    const [stagingPersonas, setStagingPersonas] = useState<ModeOption[]>([]);
    const [renovationStyles, setRenovationStyles] = useState<ModeOption[]>([]);
    const [interiorStyles, setInteriorStyles] = useState<InteriorStyle[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadOptions = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Cast to any to bypass TypeScript errors for new tables not yet in generated types
            const db = supabase as any;

            // Fetch all options in parallel
            const [togglesResult, stagingResult, renovationResult, stylesResult] = await Promise.all([
                db
                    .from('ai_enhance_toggles')
                    .select('*')
                    .eq('is_active', true)
                    .order('sort_order'),
                db
                    .from('ai_mode_options')
                    .select('*')
                    .eq('mode', 'staging')
                    .eq('is_active', true)
                    .order('sort_order'),
                db
                    .from('ai_mode_options')
                    .select('*')
                    .eq('mode', 'renovation')
                    .eq('is_active', true)
                    .order('sort_order'),
                db
                    .from('ai_interior_styles')
                    .select('*')
                    .eq('is_active', true)
                    .order('sort_order'),
            ]);

            if (togglesResult.data) {
                setEnhanceToggles(togglesResult.data as EnhanceToggle[]);
            }
            if (stagingResult.data) {
                setStagingPersonas(stagingResult.data as ModeOption[]);
            }
            if (renovationResult.data) {
                setRenovationStyles(renovationResult.data as ModeOption[]);
            }
            if (stylesResult.data) {
                setInteriorStyles(stylesResult.data as InteriorStyle[]);
            }

            // Check for errors
            const errors = [
                togglesResult.error,
                stagingResult.error,
                renovationResult.error,
                stylesResult.error
            ].filter(Boolean);

            if (errors.length > 0) {
                console.error('Error loading render options:', errors);
                setError('Failed to load some options');
            }
        } catch (err) {
            console.error('Error in useRenderOptions:', err);
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadOptions();
    }, [loadOptions]);

    // Get default toggle states
    const getDefaultToggles = useCallback((): Record<string, boolean> => {
        const defaults: Record<string, boolean> = {};
        enhanceToggles.forEach(toggle => {
            defaults[toggle.toggle_key] = toggle.default_enabled;
        });
        return defaults;
    }, [enhanceToggles]);

    // Get prompt fragments for enabled toggles
    const getPromptFragments = useCallback((enabledToggles: Record<string, boolean>): string => {
        return enhanceToggles
            .filter(toggle => enabledToggles[toggle.toggle_key])
            .map(toggle => toggle.prompt_fragment)
            .filter(Boolean)
            .join('. ');
    }, [enhanceToggles]);

    // Get style prompt value by mode and key
    const getStylePromptValue = useCallback((mode: string, key: string): string => {
        let value: string | undefined | null;

        if (mode === 'staging') {
            const item = stagingPersonas.find(p => p.option_key === key);
            value = item?.prompt_fragment;
        } else if (mode === 'renovation') {
            const item = renovationStyles.find(p => p.option_key === key);
            value = item?.prompt_fragment;
        } else {
            // Default check interior styles
            const item = interiorStyles.find(s => s.style_key === key);
            value = item?.prompt_value;
        }

        return value || key; // Fallback to key if no prompt value found
    }, [stagingPersonas, renovationStyles, interiorStyles]);

    return {
        enhanceToggles,
        stagingPersonas,
        renovationStyles,
        interiorStyles,
        loading,
        error,
        getDefaultToggles,
        getPromptFragments,
        getStylePromptValue,
        refresh: loadOptions,
    };
}

